import { BrowserEventName, GestureType, PseudoEventName } from '../../gen-src/preslayer/Enums';
import { EventHandleSpec, NormalizedEvent } from '../EventUtils';
import { Gesture, GestureEventInfo } from './Gesture';
import { GlobalUIEvents } from '../GlobalUIEvents';
import { MiscUtil } from '../utility/MiscUtil';

/**
 * Implements the tap, press, and double tap discrete gestures
 */
class Discrete extends Gesture {
  private doneFirstTap?: boolean = null;
  private startX: number = 0;
  private startY: number = 0;
  private possible: boolean = false;
  private startTime: number = 0;

  /**
   * Initializes a new instance of the Discrete class
   * @param {EventHandleSpec} config - GestureHandleHandleSpec object
   */
  public constructor(config: EventHandleSpec) {
    super(config, GestureType.Discrete, /*hasSlop*/true);
  }

  public Start(e: NormalizedEvent): void {
    let touch = e.touches[0];
    if (e.touches.length !== this.numTouches) {
      this.Reset();
      return;
    }

    let param = this.GetParams(e);
    if (!this.ProcessGlobalEvent(GlobalUIEvents.FireFirstTouch, e)) {
      return;
    }

    // regular firstTouch happens only if not canceled elsewhere
    this.Fire(PseudoEventName.FirstTouch, e, param);
    this.startX = touch.pageX;
    this.startY = touch.pageY;
    this.possible = true;
    this.pressTimer = MiscUtil.ClearTimeout(this.pressTimer);

    if (MiscUtil.IsNullOrUndefined(this.doneFirstTap)) {
      this.startTime = new Date().getTime();
      this.potentialPressTimer = window.setTimeout(() => this.DoPotentialPress(e, param), Gesture.PotentialPressTime);
      this.pressTimer = window.setTimeout(() => this.DoPress(e, param), Gesture.PressTime);
    }
  }

  public Move(e: NormalizedEvent): void {
    if (this.PastSlop(e)) {
      this.Reset();
    }
    if (e.type === BrowserEventName.TouchMove) {
      this.Fire(PseudoEventName.TouchMove, e, this.GetParams(e));
    }
  }

  public End(e: NormalizedEvent): void {
    let param: GestureEventInfo = this.GetParams(e);
    if (this.isActivePress) {
      this.DoPressEnd(e, param);
    }
    this.CancelPress();
    // bugzid 39261: we only want to recognize left clicks for mouse events
    if (this.possible && (!e.IsMouseEvent || e.IsLeftMouseEvent)) {
      // first tap = potential tap, second tap = double tap
      if (!MiscUtil.IsNullOrUndefined(this.doneFirstTap) && this.doneFirstTap) {
        this.DoDoubleTap(e, param);
      } else {
        this.DoTap(e, param);
      }
    }

    // if we get a ContextMenu event, it means right-mouse was used and mouse up has
    // already been processed (ignored), so this.possible is going to be false
    if (e.type === BrowserEventName.ContextMenu) {
      if (!this.ProcessGlobalEvent(GlobalUIEvents.FireRightClick, e)) {
        return;
      }
      // TFSID888067 - When right click dragging for Aggregation pill, releasing triggers the right click on the
      // target pill, which shouldn't happen and doesn't happen on Desktop.
      // If coming out of a drag, we do not trigger the rightclick event and prevents the native contextmenu from
      // showing up.
      if (this.PastSlop(e)) {
        e.preventDefault();
      } else {
        this.DoRightClick(e, param);
      }
    }
    if (e.touches.length === 0) {
      if (!this.ProcessGlobalEvent(GlobalUIEvents.FireLastTouch, e)) {
        return;
      }
      this.Fire(PseudoEventName.LastTouch, e, param);
    }
  }

  public Cancel(): void {
    this.Reset();
  }

  public Reset(): void {
    this.CancelPress();
    this.possible = false;
    this.doneFirstTap = null;
    this.isPotentialPress = false;
    this.isActivePress = false;
    this.doubleTapTimer = MiscUtil.ClearTimeout(this.doubleTapTimer);
  }

  public DoEvent(eventName: PseudoEventName, e: NormalizedEvent, param: GestureEventInfo, options?: Object): void {
    this.Fire(eventName, e, param);
    // tslint:disable-next-line:no-string-literal no-any whitespace strict-boolean-expressions
    if (options && (<any>options)['reset']) {
      this.Reset();
    }
  }

  public DoDoubleTap(e: NormalizedEvent, param: GestureEventInfo): void {
    this.doubleTapTimer = MiscUtil.ClearTimeout(this.doubleTapTimer);
    this.DoEvent(PseudoEventName.DoubleTap, e, param, { reset: true });
  }
  public DoPress(e: NormalizedEvent, param: GestureEventInfo): void {
    this.isPotentialPress = false;
    if (!this.ProcessGlobalEvent(GlobalUIEvents.FirePress, e)) {
      return;
    }
    this.DoEvent(PseudoEventName.Press, e, param, { reset: true });
    this.isActivePress = true;
  }

  public DoPotentialPress(e: NormalizedEvent, param: GestureEventInfo): void {
    this.isPotentialPress = true;
    this.DoEvent(PseudoEventName.PotentialPress, e, param);
  }

  public DoPressEnd(e: NormalizedEvent, param: GestureEventInfo): void {
    this.DoEvent(PseudoEventName.PressEnd, e, param);
    this.isActivePress = false;
  }

  public CancelPress(): void {
    this.potentialPressTimer = MiscUtil.ClearTimeout(this.potentialPressTimer);
    this.pressTimer = MiscUtil.ClearTimeout(this.pressTimer);
    if (this.isPotentialPress) {
      this.DoEvent(PseudoEventName.CancelPotentialPress, {} as NormalizedEvent, null);
    }
  }

  public DoTap(e: NormalizedEvent, param: GestureEventInfo): void {
    // this is called on finger-up, so it's still just a potential tap
    this.doneFirstTap = true;
    if (!this.ProcessGlobalEvent(GlobalUIEvents.FireTap, e)) {
      return;
    }

    // only set the double tap timer if the config says we are looking for double taps
    if (this.Config.IsHandling(PseudoEventName.DoubleTap)) {
      this.doubleTapTimer = window.setTimeout(() => {
        this.Reset();
        this.Fire(PseudoEventName.CancelPotentialDoubleTap, e, param);
      },
        // tslint:disable-next-line:align
        Gesture.DoubleTapTime - this.GetElapsedTime());
    } else {
      this.Reset();
    }

    // B38851 LOGIC CHANGE: fire tap immediately, without waiting to decide double-tap-ness
    this.Fire(PseudoEventName.Tap, e, param);
  }
  public DoRightClick(e: NormalizedEvent, param: GestureEventInfo): void {
    this.Fire(PseudoEventName.RightClick, e, param);
  }

  /**
  * Publishes the global event and waits for handlers to handle the event before returning
  * @param {*} eventInvoker - Action that will fire appropriate event
  * @param {NormalizedEvent} e - the event to sent to the handlers
  * @returns true if the event was not canceled
  */
  public ProcessGlobalEvent(eventInvoker: (ne: NormalizedEvent, cb: (b: boolean) => void) => void, e: NormalizedEvent): boolean {
    let canceled = false;

    let cancelCallback = (preventDefault: boolean) => {
      this.Reset();
      if (preventDefault) {
        // Copied from EventUtil.StopEvent
        e.stopPropagation();
        e.preventDefault();
      }
      canceled = true;
    };

    eventInvoker(e, cancelCallback);

    return !canceled;
  }

  public GetElapsedTime(): number {
    return new Date().getTime() - this.startTime;
  }

  public PastSlop(e: NormalizedEvent): boolean {
    let touch: Touch = e.changedTouches[0];
    let zoomAdjustedSlop: number = this.ZoomAdjustedSlop;
    return Math.abs(touch.pageX - this.startX) > zoomAdjustedSlop || Math.abs(touch.pageY - this.startY) > zoomAdjustedSlop;
  }

  private GetParams(e: NormalizedEvent): GestureEventInfo {
    let param: GestureEventInfo = new GestureEventInfo();
    param.pageX = e.changedTouches[0].pageX;
    param.pageY = e.changedTouches[0].pageY;
    param.clientX = e.changedTouches[0].clientX;
    param.clientY = e.changedTouches[0].clientY;

    // For discrete gestures, (PageX, PageY) is also the start of the gesture.
    param.startX = e.changedTouches[0].pageX;
    param.startY = e.changedTouches[0].pageY;
    return param;
  }
}

export { Discrete };
